import type { Subscription } from '../types';

/**
 * True when a checkout session's plan_name is a UISP plan. billing-status-worker passes
 * Stripe's line item description, i.e. the product name (e.g. "UISP U25"), never a bare
 * plan code. UniFi names (P25, P25 Annual) never contain "UISP"; missing is false.
 */
export function isUispCheckoutPlan(planName: string | null | undefined): boolean {
  return !!planName && /\bUISP\b/i.test(planName);
}

export function getServiceLabel(appName: 'UniFi' | 'UISP', isMigrated: boolean): string {
  if (isMigrated) return 'UOS';
  if (appName === 'UISP') return 'UISP';
  return 'UniFi Controller';
}

/**
 * A UISP hosted as a uisp-<slug> container on one of our nodes (UISP Phase 3). The API
 * sends netflow_port only for those rows; a legacy VPS UISP has null. Drives the NetFlow
 * line and the custom domain tab on the service page.
 */
export function isNodeUisp(sub: { app_name: 'UniFi' | 'UISP'; netflow_port?: number | null }): boolean {
  return sub.app_name === 'UISP' && sub.netflow_port != null;
}

/**
 * The ONE name the portal shows for a service (design 2026-10-30 §20): the UniFi OS console
 * name for a UOS service, a node UISP's <slug>.netconsole.cloud name when the API sends one,
 * else the hostname (pre-amendment UISP, classic UniFi, VPS UISP). Custom domains stay the
 * caller's choice (the header and Open button prefer the first one, as before).
 */
export function primaryHostname(
  sub: Pick<Subscription, 'hostname' | 'app_name'> & Partial<Pick<Subscription, 'migrated_to_uos' | 'uos_hostname' | 'public_hostname' | 'netflow_port'>>,
): string {
  if (sub.migrated_to_uos && sub.uos_hostname) return sub.uos_hostname;
  if (isNodeUisp(sub) && sub.public_hostname) return sub.public_hostname;
  return sub.hostname;
}

export type DeviceLimitState = 'over' | 'near' | 'ok';

/**
 * Classifies device usage against a plan's limit.
 *
 * 'over' — strictly over max (current === max is NOT over).
 * 'near' — at or above 95% of max but not over.
 * 'ok'   — comfortably under 95%.
 *
 * Single source of truth for the 95% threshold — DeviceCount and ServiceCard
 * both render limit notices off this and must agree on where the line is.
 */
export function deviceLimitState(current: number, max: number): DeviceLimitState {
  if (current > max) return 'over';
  if ((current / max) * 100 >= 95) return 'near';
  return 'ok';
}

/**
 * Builds the `/billing?upgrade=<id>` deep link consumed by useUpgradeDeepLink.
 *
 * Encodes the id to match the paired portal-api email (services/email.ts),
 * which wraps the same value in encodeURIComponent — the two halves of one
 * contract should agree even though subscription ids are hostname-shaped
 * and practically safe unencoded.
 */
export function upgradeHref(subscriptionId: string): string {
  return `/billing?upgrade=${encodeURIComponent(subscriptionId)}`;
}

/**
 * Builds the `/services/<id>?migrate=uos` deep link that ServiceDetailPage turns into the
 * Controls tab with the Upgrade to UniFi OS window open. Not `?upgrade=`: that one is the
 * plan-upgrade link above.
 */
export function uosUpgradeHref(subscriptionId: string): string {
  return `/services/${encodeURIComponent(subscriptionId)}?migrate=uos`;
}
